import React, { useRef, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const PORTRAIT_SRC =
  "https://images.unsplash.com/photo-1762293274512-bcc4cf82400a?auto=format&fit=crop&w=900&q=80";

/* ---- fallback: drawn headphones silhouette (used if photo fails) ---- */
function drawFallback(ctx, w, h) {
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, h);
  const s = Math.min(w, h);
  const cx = w / 2;
  const cy = h * 0.4;
  const headR = s * 0.17;
  ctx.fillStyle = "#fff";
  ctx.strokeStyle = "#fff";
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.ellipse(cx, h * 1.02, s * 0.44, s * 0.34, 0, Math.PI, 0, true);
  ctx.fill();
  ctx.fillRect(cx - headR * 0.45, cy + headR * 0.55, headR * 0.9, headR * 0.9);
  ctx.beginPath();
  ctx.arc(cx, cy, headR, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = s * 0.055;
  ctx.beginPath();
  ctx.arc(cx, cy, headR * 1.32, Math.PI * 1.12, Math.PI * 1.88);
  ctx.stroke();
}

/* draw an <img> into the mask canvas using object-fit: cover, centered */
function drawImageCover(ctx, img, w, h) {
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, h);
  const ir = img.width / img.height;
  const cr = w / h;
  let dw, dh, dx, dy;
  if (ir > cr) {
    dh = h;
    dw = h * ir;
    dx = (w - dw) / 2;
    dy = 0;
  } else {
    dw = w;
    dh = w / ir;
    dx = 0;
    dy = (h - dh) / 2;
  }
  ctx.drawImage(img, dx, dy, dw, dh);
}

const SNOISE = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);
  const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));
  vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);
  vec3 l=1.0-g;
  vec3 i1=min(g.xyz,l.zxy);
  vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;
  vec3 x2=x0-i2+C.yyy;
  vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(
    i.z+vec4(0.0,i1.z,i2.z,1.0))
    +i.y+vec4(0.0,i1.y,i2.y,1.0))
    +i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;
  vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);
  vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy;
  vec4 y=y_*ns.x+ns.yyyy;
  vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);
  vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0;
  vec4 s1=floor(b1)*2.0+1.0;
  vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
  vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);
  vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);
  vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
  m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}`;

const VERT = `
uniform float uTime;
uniform vec2  uMouse;
uniform float uMouseStrength;
uniform float uPointSize;
uniform float uMotion;
uniform float uAudio;
attribute vec3 aHome;
attribute float aSize;
attribute float aAlpha;
varying float vAlpha;
${SNOISE}
void main(){
  vec3 pos = aHome;
  float t = uTime * 0.15;
  float nx = snoise(vec3(aHome.xy * 1.2, t));
  float ny = snoise(vec3(aHome.xy * 1.2, t + 100.0));
  float drift = 0.012 * uMotion + uAudio * 0.010;
  pos.x += nx * drift;
  pos.y += ny * drift;

  // gentle audio pulse: breathe outward from center
  pos.xy *= 1.0 + uAudio * 0.035;

  vec2 toMouse = pos.xy - uMouse;
  float dist = length(toMouse);
  float radius = 0.28;
  float falloff = smoothstep(radius, 0.0, dist);
  vec2 push = normalize(toMouse + 0.0001) * falloff * 0.18 * uMouseStrength;
  pos.xy += push;

  vAlpha = aAlpha;
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = aSize * (uPointSize / -mv.z) * (1.0 + uAudio * 0.4);
}`;

const FRAG = `
uniform float uAudio;
varying float vAlpha;
void main(){
  float d = distance(gl_PointCoord, vec2(0.5));
  float a = smoothstep(0.5, 0.0, d) * vAlpha;
  if (a < 0.01) discard;
  vec3 col = vec3(0.92, 0.94, 0.98) + uAudio * 0.06;
  gl_FragColor = vec4(col, a);
}`;

function Particles({ reduceMotion, levelRef }) {
  const matRef = useRef();
  const { size } = useThree();
  const mouse = useRef(new THREE.Vector2(999, 999));
  const strength = useRef(0);
  const [built, setBuilt] = useState(null);
  const dpr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio : 1, 2);

  useEffect(() => {
    let cancelled = false;
    const cw = Math.min(Math.floor(size.width) || 600, 900);
    const ch = Math.min(Math.floor(size.height) || 700, 1000);

    const build = (ctx) => {
      if (cancelled) return;
      const data = ctx.getImageData(0, 0, cw, ch).data;
      const aspect = cw / ch;
      let step = window.innerWidth < 768 ? 5 : 3;
      const collect = (st) => {
        const homes = [], sizes = [], alphas = [];
        for (let y = 0; y < ch; y += st) {
          for (let x = 0; x < cw; x += st) {
            const i = (y * cw + x) * 4;
            const b = (data[i] * 0.3 + data[i + 1] * 0.59 + data[i + 2] * 0.11) / 255;
            if (b > 0.16) {
              const u = x / cw;
              const v = y / ch;
              homes.push((u - 0.5) * 2 * aspect, (0.5 - v) * 2, (Math.random() - 0.5) * 0.05);
              sizes.push(1.0 + Math.random() * 1.1);
              alphas.push(0.35 + b * 0.65);
            }
          }
        }
        return { homes, sizes, alphas };
      };
      let res = collect(step);
      while (res.homes.length / 3 > 34000) {
        step += 1;
        res = collect(step);
      }
      const geo = new THREE.BufferGeometry();
      const positions = new Float32Array(res.homes);
      geo.setAttribute("position", new THREE.BufferAttribute(positions.slice(), 3));
      geo.setAttribute("aHome", new THREE.BufferAttribute(positions, 3));
      geo.setAttribute("aSize", new THREE.BufferAttribute(new Float32Array(res.sizes), 1));
      geo.setAttribute("aAlpha", new THREE.BufferAttribute(new Float32Array(res.alphas), 1));
      const mat = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uMouse: { value: new THREE.Vector2(999, 999) },
          uMouseStrength: { value: 0 },
          uPointSize: { value: 5.0 * dpr },
          uMotion: { value: reduceMotion ? 0 : 1 },
          uAudio: { value: 0 },
        },
        vertexShader: VERT,
        fragmentShader: FRAG,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      setBuilt((prev) => {
        if (prev) {
          prev.geometry.dispose();
          prev.material.dispose();
        }
        return { geometry: geo, material: mat };
      });
    };

    const cvs = document.createElement("canvas");
    cvs.width = cw;
    cvs.height = ch;
    const ctx = cvs.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      drawImageCover(ctx, img, cw, ch);
      build(ctx);
    };
    img.onerror = () => {
      drawFallback(ctx, cw, ch);
      build(ctx);
    };
    img.src = PORTRAIT_SRC;
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.width, size.height]);

  useEffect(() => {
    if (reduceMotion) return;
    const el = document.getElementById("portrait-canvas-wrap");
    if (!el) return;
    const aspect = (size.width || 600) / (size.height || 700);
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = (e.clientY - rect.top) / rect.height;
      mouse.current.set((nx - 0.5) * 2 * aspect, (0.5 - ny) * 2);
      strength.current = 1;
    };
    const onLeave = () => { strength.current = 0; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [size.width, size.height, reduceMotion]);

  useFrame(({ clock }) => {
    if (!built) return;
    const m = built.material;
    m.uniforms.uTime.value = clock.getElapsedTime();
    m.uniforms.uMouse.value.copy(mouse.current);
    const target = strength.current;
    m.uniforms.uMouseStrength.value += (target - m.uniforms.uMouseStrength.value) * 0.08;
    const lvl = levelRef && levelRef.current ? levelRef.current : 0;
    m.uniforms.uAudio.value += (lvl - m.uniforms.uAudio.value) * 0.1;
  });

  if (!built) return null;
  return <points geometry={built.geometry} material={built.material} />;
}

export default function ParticlePortrait({ levelRef }) {
  const [reduceMotion, setReduceMotion] = useState(false);
  const [webgl, setWebgl] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    try {
      const c = document.createElement("canvas");
      const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
      if (!gl) setWebgl(false);
    } catch (e) {
      setWebgl(false);
    }
  }, []);

  if (!webgl) {
    return <div className="portrait-fallback" aria-label="Portrait made of light particles" role="img" />;
  }

  return (
    <div
      id="portrait-canvas-wrap"
      className="portrait-canvas-wrap"
      role="img"
      aria-label="Animated portrait made of light particles"
    >
      <div className="portrait-glow" />
      <Canvas
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
        dpr={[1, 2]}
        camera={{ fov: 45, position: [0, 0, 2.7], near: 0.1, far: 100 }}
      >
        <Particles reduceMotion={reduceMotion} levelRef={levelRef} />
      </Canvas>
    </div>
  );
}
